import axios from "axios";
import moment from "moment";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { useDispatch } from "react-redux";
import { getProfileData } from "../services/ProfileService";
import { setLoader } from "../Store/Reducers/UserReducer";
import TreatmentWithUs from "./MedicalHistory Component/TreatmentWithUs";

// ── Landing page ─────────────────────────────────────────────────────────────
//
// What the patient sees on sign-in. It follows the clinical OX.ar patient
// overview: a strip of counts, the patient and their partner side by side,
// and the treatment cycles underneath.
//
// PATIENT-SAFE BY DESIGN. Everything here is the patient's own information.
// Clinic notes and activity notes are not requested or shown, and the results
// count is of APPROVED results only (see approvedSets in ObservationResult.js
// for the same rule applied to the results pages).

const DEFAULT_AVATAR = null;

const isBlank = (v) => v === null || v === undefined || ["", "n/a", "na", "null"].includes(String(v).trim().toLowerCase());
const dateOnly = (raw) => {
  if (isBlank(raw) || String(raw).startsWith("0001-01-01")) return null;
  const m = moment(raw);
  return m.isValid() && m.year() > 1 ? m : null;
};

// Same weight-band wording the clinical overview prints under the BMI.
function bmiBand(bmi) {
  if (bmi < 18.5) return "Underweight";
  if (bmi < 25) return "Normal";
  if (bmi < 30) return "Overweight";
  return "Obese";
}

function bmiOf(q) {
  const h = Number(q?.heightinMetres);
  const w = Number(q?.weightinKilograms);
  if (!(h > 0) || !(w > 0)) return null;
  const v = w / (h * h);
  return Number.isFinite(v) ? v : null;
}

function initials(first, last) {
  return `${(first || "").charAt(0)}${(last || "").charAt(0)}`.toUpperCase() || "?";
}

function Avatar({ image, first, last }) {
  if (image) {
    return <img className="ox-avatar" src={`data:image/png;base64,${image}`} alt="" />;
  }
  return <span className="ox-avatar ox-avatar--initials" aria-hidden="true">{initials(first, last)}</span>;
}

function PersonCard({ role, person, bmi, allergies, active, loading }) {
  if (!person) {
    return (
      <section className="ox-person ox-person--empty" aria-label={role}>
        <span className="ox-role">{role}</span>
        <p className="ox-muted">{loading ? "Loading…" : "No partner details on record."}</p>
      </section>
    );
  }
  const dob = dateOnly(person.dob);
  return (
    <section className={`ox-person${active ? " ox-person--active" : ""}`} aria-label={role}>
      <div className="ox-person__top">
        <Avatar image={person.image} first={person.first} last={person.last} />
        <div className="ox-person__id">
          <div className="ox-person__chips">
            <span className="ox-role">{role}</span>
            {active && <span className="ox-chip ox-chip--viewing">You</span>}
          </div>
          <h2 className="ox-person__name">{[person.first, person.last].filter(Boolean).join(" ") || person.fullname}</h2>
        </div>
        {bmi !== null && bmi !== undefined && (
          <div className="ox-bmi">
            <strong>{bmi.toFixed(1)}</strong>
            <span>BMI · {bmiBand(bmi)}</span>
          </div>
        )}
      </div>
      <p className="ox-person__facts">
        {dob && <span>DOB {dob.format("YYYY-MM-DD")}{person.age !== undefined && person.age !== null ? ` · ${person.age}y` : ""}</span>}
        {person.gender && <span>{person.gender}</span>}
        {person.email && <span>{person.email}</span>}
        {person.mobile && <span>{person.mobile}</span>}
      </p>
      {allergies && (
        allergies.length === 0
          ? <span className="ox-chip ox-chip--ok">No known allergy</span>
          : allergies.slice(0, 4).map((a) => (
              <span key={a} className="ox-chip ox-chip--warn">{a}</span>
            ))
      )}
    </section>
  );
}

export default function PatientOverview() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const url = process.env.REACT_APP_OXAR_API_URL;
  const uID = localStorage.getItem("UserID");

  const [profile, setProfile] = useState(null);
  const [partner, setPartner] = useState(null);
  const [questionnaire, setQuestionnaire] = useState(null);
  const [allergies, setAllergies] = useState(null);
  const [counts, setCounts] = useState({ appts: null, drugs: null, results: null });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    const quiet = (p) => p.catch(() => null);

    (async () => {
      dispatch(setLoader(true));
      const [prof, rel, q, al, ap, rx, obs] = await Promise.all([
        quiet(getProfileData(uID)),
        quiet(axios(`${url}api/CrmUser/GetRelatedContactByID?Id=${uID}`)),
        quiet(axios(`${url}api/Profile/GetQuestionairebyPatientId?id=${uID}`)),
        quiet(axios(`${url}api/Allergy/GetByContactId?id=${uID}`)),
        quiet(axios(`${url}api/AppointmentDosage/GetAppointments?contactId=${uID}`)),
        quiet(axios(`${url}api/Prescriptions/GetByContactId?id=${uID}`)),
        // The order list, not the result lines: it carries the approval flag.
        quiet(axios(`${url}api/TDLObservation/GetObservationByContactId?id=${uID}`)),
      ]);
      if (!alive) return;

      const p = prof?.content || null;
      if (p) {
        try { localStorage.setItem("UData", JSON.stringify(p)); } catch { /* convenience only */ }
      }
      setProfile(p);

      const r = rel?.data?.content || null;
      if (r) {
        try { sessionStorage.setItem("ReleatedcontactData", JSON.stringify(r)); } catch { /* convenience only */ }
      }
      setPartner(r);
      setQuestionnaire(q?.data?.content || null);
      setAllergies(
        Array.isArray(al?.data?.content)
          ? al.data.content.map((a) => a.name).filter((n) => !isBlank(n))
          : null,
      );

      const list = (res) => (Array.isArray(res?.data?.content) ? res.data.content : null);
      const appts = list(ap);
      const now = moment();
      setCounts({
        appts: appts ? appts.filter((a) => moment(a.startTime).isSameOrAfter(now, "day")).length : null,
        drugs: list(rx)?.length ?? null,
        // Approved results only — an unapproved result is not the patient's to see yet.
        results: list(obs)?.filter((o) => o.approve === true).length ?? null,
      });
      setLoading(false);
      dispatch(setLoader(false));
    })();

    return () => { alive = false; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const patient = useMemo(() => profile && ({
    first: profile.firstName,
    last: profile.lastName,
    dob: profile.birthday,
    age: profile.age,
    gender: profile.gendercodeName,
    email: profile.email,
    mobile: profile.mobileNumber,
    image: profile.entityimage,
  }), [profile]);

  const partnerPerson = useMemo(() => partner && ({
    fullname: partner.fullname,
    first: partner.firstname || partner.firstName,
    last: partner.lastname || partner.lastName,
    dob: partner.birthdate,
    age: partner.age,
    gender: partner.gendercodeName || partner.gender,
    email: partner.emailaddress1 || partner.email,
    mobile: partner.mobilephone || partner.mobileNumber,
    image: partner.entityimage,
  }), [partner]);

  const stat = (n, label, to) => (
    <button type="button" className="ox-stat" onClick={() => navigate(to)}>
      <strong>{n === null ? "–" : n}</strong> {label}
    </button>
  );

  return (
    <div className="ox-overview">
      <nav className="ox-crumbs" aria-label="Breadcrumb">
        <span>Home</span><span aria-hidden="true">/</span><strong>Overview</strong>
      </nav>

      <div className="ox-strip" role="group" aria-label="At a glance">
        {stat(counts.appts, "upcoming appts", "/Appointment")}
        {stat(counts.drugs, "prescriptions", "/Prescription")}
        {stat(counts.results, "approved results", "/ObservationResult")}
        <span className="ox-strip__who">
          {patient && <><span className="ox-strip__label">Patient</span> {patient.first} {patient.last}</>}
        </span>
        <button type="button" className="ox-btn" onClick={() => window.location.reload()}>Refresh</button>
      </div>

      <div className="ox-people">
        <PersonCard
          role="Patient"
          person={patient}
          bmi={bmiOf(questionnaire)}
          allergies={allergies}
          active
          loading={loading}
        />
        <PersonCard role="Partner" person={partnerPerson} loading={loading} />
      </div>

      <section className="ox-treatments" aria-label="Treatments">
        <TreatmentWithUs standalone embedded />
      </section>
    </div>
  );
}
